import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import ReportWidget from './components/ReportWidget';
import { DataPoint } from './components/DataPoint';
import { formatCurrency as formatCurrencyBase } from '../../helpers/formatCurrency';

const COLORS = {
  labor: '#3b82f6',
  parts: '#f59e0b',
  contractor: '#10b981',
};

const EM_DASH = '—';

// Summary widgets render without decimals; the shared helper handles locale + symbol.
const formatCurrency = (value, currency) =>
  formatCurrencyBase(value, currency, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const safePercent = (part, whole) => {
  if (!whole || whole === 0) return 0;
  const n = typeof part === 'string' ? parseFloat(part) : (part || 0);
  const w = typeof whole === 'string' ? parseFloat(whole) : whole;
  if (!w || Number.isNaN(w)) return 0;
  return Math.round((n / w) * 100);
};

const CostTooltip = ({ active, payload, currency, t }) => {
  if (!active || !payload || !payload.length) return null;
  const dataPoint = payload[0]?.payload;
  if (!dataPoint) return null;
  return (
    <div
      style={{
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: 12,
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
        fontSize: '0.85rem',
        minWidth: 180,
      }}
    >
      <div className="fw-bold mb-2">{dataPoint.periodLabel}</div>
      {[
        { key: 'labor', color: COLORS.labor, label: t('reporting.cost.summary.labor', 'Labor') },
        { key: 'parts', color: COLORS.parts, label: t('reporting.cost.summary.parts', 'Parts') },
        { key: 'contractor', color: COLORS.contractor, label: t('reporting.cost.summary.contractor', 'Contractor') },
      ].map((row) => (
        <div key={row.key} className="d-flex align-items-center gap-2">
          <span style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: row.color, display: 'inline-block' }} />
          <span className="text-muted">{row.label}</span>
          <span className="fw-bold ms-auto">{formatCurrency(dataPoint[row.key], currency)}</span>
        </div>
      ))}
      <div className="d-flex align-items-center gap-2 mt-1 pt-1 border-top">
        <span className="text-muted">{t('reporting.cost.summary.total', 'Total')}</span>
        <span className="fw-bold ms-auto">{formatCurrency(dataPoint.total, currency)}</span>
      </div>
    </div>
  );
};

/**
 * Spend summary + stacked-bar trend for the Work Order Cost Report.
 * Fetched centrally by the dashboard — receives `data` / `loading` / `error` as props.
 * The title is clickable (via {@link onDetailsClick}) and routes to the Reporting Details
 * tab where the per-WO cost table is rendered.
 */
const WorkOrderSpendWidget = ({ data, loading, error, onDetailsClick }) => {
  const { t } = useTranslation();
  const summary = data?.summary;
  const chartData = data?.chartData || [];
  const currency = summary?.currency;
  const totalSpend = summary?.totalSpend ?? 0;
  const laborTotal = summary?.laborTotal ?? 0;
  const partsTotal = summary?.partsTotal ?? 0;
  const contractorTotal = summary?.contractorTotal ?? 0;
  const isEmpty = !summary || !chartData.length || (Number(totalSpend) === 0);

  return (
    <ReportWidget
      title={t('reporting.cost.spend.title', 'Work order spend')}
      loading={loading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage={t('reporting.cost.spend.empty', 'No cost data for the selected period.')}
      onDetailsClick={onDetailsClick}
    >
      {summary && (
        <div className="d-flex flex-wrap justify-content-center justify-content-md-end align-items-center gap-3 gap-md-5 mb-3 py-2">
          <DataPoint
            value={formatCurrency(totalSpend, currency)}
            label={t('reporting.cost.summary.total_spend', 'Total spend')}
            color="#6b7280"
          />

          <div className="border-end d-none d-md-block" style={{ height: '60px' }} />

          <DataPoint
            value={formatCurrency(laborTotal, currency)}
            label={`${t('reporting.cost.summary.labor', 'Labor')} · ${safePercent(laborTotal, totalSpend)}%`}
            color={COLORS.labor}
          />
          <DataPoint
            value={formatCurrency(partsTotal, currency)}
            label={`${t('reporting.cost.summary.parts', 'Parts')} · ${safePercent(partsTotal, totalSpend)}%`}
            color={COLORS.parts}
          />
          <DataPoint
            value={formatCurrency(contractorTotal, currency)}
            label={`${t('reporting.cost.summary.contractor', 'Contractor')} · ${safePercent(contractorTotal, totalSpend)}%`}
            color={COLORS.contractor}
          />
        </div>
      )}

      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="periodLabel" stroke="#666" tick={{ fontSize: 12 }} />
          <YAxis
            stroke="#666"
            tick={{ fontSize: 12 }}
            tickFormatter={(v) => formatCurrency(v, currency)}
            width={80}
          />
          <Tooltip content={<CostTooltip currency={currency} t={t} />} />
          <Legend />
          <Bar
            dataKey="labor"
            stackId="cost"
            fill={COLORS.labor}
            name={t('reporting.cost.summary.labor', 'Labor')}
          />
          <Bar
            dataKey="parts"
            stackId="cost"
            fill={COLORS.parts}
            name={t('reporting.cost.summary.parts', 'Parts')}
          />
          <Bar
            dataKey="contractor"
            stackId="cost"
            fill={COLORS.contractor}
            name={t('reporting.cost.summary.contractor', 'Contractor')}
          />
        </BarChart>
      </ResponsiveContainer>
    </ReportWidget>
  );
};

export default WorkOrderSpendWidget;
